import { ReactElement, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { UIText } from '../ui-text';
import styles from './ui-date-range-picker.module.scss';
import { blue, blue10, blue30, charcoal, concrete } from '../../styles';
import { UIIcon } from '../ui-icon';

const ITEM_WIDTH = 40;
const CALENDAR_WIDTH = ITEM_WIDTH * 7;
export const DATE_RANGE_PICKER_WIDTH = CALENDAR_WIDTH * 2 + 24 + 1;

type StartOfWeek = 'monday' | 'sunday';

type UIDateRangePickerProps = {
    from: Date | null;
    to: Date | null;
    timezone: string;
    startOfWeek?: StartOfWeek;
    onChange: (from: Date, to: Date) => void;
};

export function UIDateRangePicker(props: UIDateRangePickerProps): ReactElement {
    const { from, to, timezone, startOfWeek = 'monday', onChange } = props;

    const [leftMonth, setLeftMonth] = useState(() => {
        if (from !== null) {
            return dateToFirstDayOfMonth(from, timezone);
        } else {
            return dateToFirstDayOfMonth(new Date(), timezone);
        }
    });
    const [rightMonth, setRightMonth] = useState(() => {
        if (
            to !== null &&
            from !== null &&
            isDateInALaterMonth(to, from, timezone)
        ) {
            return dateToFirstDayOfMonth(to, timezone);
        }
        {
            return addMonths(leftMonth, 1, timezone);
        }
    });

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        from,
        to
    ]);
    const [currentHoverDate, setCurrentHoverDate] = useState<Date | null>(null);

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            onChange(dateRange[0], dateRange[1]);
        }
    }, [dateRange, onChange]);

    function onLeftPrevMonth() {
        setLeftMonth(addMonths(leftMonth, -1, timezone));
    }

    function onLeftNextMonth() {
        const newLeftMonth = addMonths(leftMonth, 1, timezone);
        if (newLeftMonth.getTime() >= rightMonth.getTime()) {
            setRightMonth(addMonths(newLeftMonth, 1, timezone));
        }
        setLeftMonth(newLeftMonth);
    }

    function onRightPrevMonth() {
        const newRightMonth = addMonths(rightMonth, -1, timezone);
        if (newRightMonth.getTime() <= leftMonth.getTime()) {
            setLeftMonth(addMonths(newRightMonth, -1, timezone));
        }
        setRightMonth(newRightMonth);
    }

    function onRightNextMonth() {
        setRightMonth(addMonths(rightMonth, 1, timezone));
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'row', gap: 24 }}>
            <Month
                month={leftMonth}
                timezone={timezone}
                startOfWeek={startOfWeek}
                dateRange={dateRange}
                currentHoverDate={currentHoverDate}
                onPrevMonth={onLeftPrevMonth}
                onNextMonth={onLeftNextMonth}
                onDateRangeChange={(dateRange: [Date | null, Date | null]) => {
                    setDateRange(dateRange);
                }}
                onDateHover={(date) => {
                    setCurrentHoverDate(date);
                }}
            />
            {/* Divider */}
            <div
                style={{
                    width: 1,

                    backgroundColor: concrete
                }}
            />
            <Month
                month={rightMonth}
                timezone={timezone}
                startOfWeek={startOfWeek}
                dateRange={dateRange}
                currentHoverDate={currentHoverDate}
                onPrevMonth={onRightPrevMonth}
                onNextMonth={onRightNextMonth}
                onDateRangeChange={(dateRange) => {
                    setDateRange(dateRange);
                }}
                onDateHover={(date) => {
                    setCurrentHoverDate(date);
                }}
            />
        </div>
    );
}

type MonthProps = {
    month: Date;
    timezone: string;
    startOfWeek: StartOfWeek;
    dateRange: [Date | null, Date | null];
    currentHoverDate: Date | null;
    onPrevMonth: () => void;
    onNextMonth: () => void;
    onDateRangeChange: (dateRange: [Date | null, Date | null]) => void;
    onDateHover: (date: Date) => void;
};
function Month(props: MonthProps): ReactElement {
    const {
        month,
        timezone,
        startOfWeek,
        dateRange,
        currentHoverDate,
        onPrevMonth,
        onNextMonth,
        onDateRangeChange,
        onDateHover
    } = props;

    const weeks = getAllDatesInCalendarView(month, startOfWeek, timezone);
    const weekHeaders = getAllWeekHeaders(startOfWeek);

    function onDateClick(date: Date) {
        const [startDate, endDate] = dateRange;

        if (!startDate) {
            onDateRangeChange([date, null]);
        } else if (startDate && !endDate) {
            if (date.getTime() >= startDate.getTime()) {
                onDateRangeChange([startDate, date]);
            } else {
                onDateRangeChange([date, startDate]);
            }
        } else if (startDate && endDate) {
            onDateRangeChange([date, null]);
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <MonthHeader
                month={month}
                timezone={timezone}
                onPrevMonth={onPrevMonth}
                onNextMonth={onNextMonth}
            />
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row'
                }}
            >
                {weekHeaders.map((text, index) => (
                    <WeekHeader key={`h${index}`} text={text} />
                ))}
            </div>
            {weeks.map((week, weekIndex) => (
                <div
                    key={`w${weekIndex}`}
                    style={{ display: 'flex', flexDirection: 'row' }}
                >
                    {week.map((date, dayIndex) => (
                        <DateItem
                            key={`d${dayIndex}`}
                            date={date}
                            timezone={timezone}
                            dateRange={dateRange}
                            hoverDate={currentHoverDate}
                            dayIndex={dayIndex}
                            isInCurrentMonth={isDateInMonth(
                                date,
                                month,
                                timezone
                            )}
                            onClick={() => {
                                onDateClick(date);
                            }}
                            onHover={() => {
                                onDateHover(date);
                            }}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

type MonthHeaderProps = {
    month: Date;
    timezone: string;
    onPrevMonth?: () => void;
    onNextMonth?: () => void;
};
function MonthHeader(props: MonthHeaderProps): ReactElement {
    const { month, timezone, onPrevMonth, onNextMonth } = props;

    const monthStrings = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
    ];

    const dt = DateTime.fromJSDate(month).setZone(timezone);

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: CALENDAR_WIDTH,
                height: ITEM_WIDTH
            }}
        >
            <button className={styles.chevronButton} onClick={onPrevMonth}>
                <UIIcon name="chevron-left" size={16} color={charcoal} />
            </button>

            <UIText>{`${monthStrings[dt.month - 1]} ${dt.year}`}</UIText>

            <button className={styles.chevronButton} onClick={onNextMonth}>
                <UIIcon name="chevron-right" size={16} color={charcoal} />
            </button>
        </div>
    );
}

type WeekHeaderProps = {
    text: string;
};
function WeekHeader(props: WeekHeaderProps): ReactElement {
    const { text } = props;
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: ITEM_WIDTH,
                height: ITEM_WIDTH
            }}
        >
            <UIText>{text}</UIText>
        </div>
    );
}

type DateItemProps = {
    date: Date;
    timezone: string;
    dateRange: [Date | null, Date | null];
    hoverDate: Date | null;
    dayIndex: number;
    isInCurrentMonth: boolean;
    onClick: () => void;
    onHover: () => void;
};
function DateItem(props: DateItemProps): ReactElement {
    const {
        date,
        timezone,
        dateRange,
        hoverDate,
        dayIndex,
        isInCurrentMonth,
        onClick,
        onHover
    } = props;

    const day = useMemo(
        () => DateTime.fromJSDate(date).setZone(timezone).day,
        [date, timezone]
    );

    const isStartRow = useMemo(() => {
        if (dayIndex === 0 || isFirstDayOfMonth(date, timezone)) {
            return true;
        }

        if (
            dateRange[0] &&
            dateRange[1] &&
            dateRange[0].getTime() === date.getTime()
        ) {
            return true;
        }
        if (dateRange[0] && !dateRange[1]) {
            if (
                dateRange[0] &&
                dateRange[0].getTime() === date.getTime() &&
                hoverDate &&
                hoverDate.getTime() >= date.getTime()
            ) {
                return true;
            }
            if (
                dateRange[0] &&
                dateRange[0].getTime() >= date.getTime() &&
                hoverDate &&
                hoverDate.getTime() === date.getTime()
            ) {
                return true;
            }
        }

        return false;
    }, [date, timezone, dateRange, hoverDate, dayIndex]);

    const isEndRow = useMemo(() => {
        if (dayIndex === 6 || isLastDayOfMonth(date, timezone)) {
            return true;
        }

        if (
            dateRange[0] &&
            dateRange[1] &&
            dateRange[1].getTime() === date.getTime()
        ) {
            return true;
        }

        if (dateRange[0] && !dateRange[1]) {
            if (
                dateRange[0] &&
                dateRange[0].getTime() <= date.getTime() &&
                hoverDate &&
                hoverDate.getTime() === date.getTime()
            ) {
                return true;
            }

            if (
                dateRange[0] &&
                dateRange[0].getTime() === date.getTime() &&
                hoverDate &&
                hoverDate.getTime() <= date.getTime()
            ) {
                return true;
            }
        }

        return false;
    }, [date, timezone, dateRange, hoverDate, dayIndex]);

    const isSelected = useMemo(
        () => doesDateEqualOneOfDateRange(dateRange, date),
        [dateRange, date]
    );

    const isBetweenRange = useMemo(
        () => doesDateRangeInterset(dateRange, date),
        [dateRange, date]
    );

    const isBetweenHoverRange = useMemo(() => {
        return (
            hoverDate !== null &&
            dateRange[0] !== null &&
            dateRange[1] === null &&
            doesDateRangeInterset([dateRange[0], hoverDate], date)
        );
    }, [date, dateRange, hoverDate]);

    if (!isInCurrentMonth) {
        return (
            <div>
                <div className={styles.emptyDateItem} />
            </div>
        );
    }

    return (
        <div
            style={{
                backgroundColor: isBetweenRange
                    ? blue30
                    : isBetweenHoverRange
                      ? blue10
                      : undefined,
                borderTopLeftRadius: isStartRow ? 999 : 0,
                borderBottomLeftRadius: isStartRow ? 999 : 0,
                borderTopRightRadius: isEndRow ? 999 : 0,
                borderBottomRightRadius: isEndRow ? 999 : 0
            }}
            onMouseOver={onHover}
        >
            <button
                className={styles.dateItem}
                style={{ backgroundColor: isSelected ? blue : undefined }}
                onClick={onClick}
            >
                <UIText type={isSelected ? 'white' : 'default'}>{day}</UIText>
            </button>
        </div>
    );
}

function getAllWeekHeaders(startOfWeek: StartOfWeek): string[] {
    const weekHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (startOfWeek === 'monday') {
        const first = weekHeaders.shift();
        weekHeaders.push(first as string);
    }

    return weekHeaders;
}

function getAllDatesInCalendarView(
    month: Date,
    startOfWeek: StartOfWeek,
    timezone: string
): Date[][] {
    const dates: Date[][] = [];

    const monthDt = DateTime.fromJSDate(month).setZone(timezone);

    // Luxon weekday: Mon=1, Sun=7. Convert to JS convention (Sun=0, Mon=1, etc.)
    const jsWeekday = monthDt.weekday % 7;

    // Determine the offset based on the start of the week
    const startOffset =
        startOfWeek === 'monday' ? (jsWeekday + 6) % 7 : jsWeekday;

    // Find the start of the calendar view
    let currentDt = monthDt.minus({ days: startOffset });

    // Iterate from the start date to the end date
    for (let i = 0; i < 6; i++) {
        dates.push([]);
        for (let j = 0; j < 7; j++) {
            dates[i].push(currentDt.toJSDate());
            currentDt = currentDt.plus({ days: 1 });
        }
    }

    return dates;
}

function isDateInMonth(date: Date, month: Date, timezone: string): boolean {
    const dateDt = DateTime.fromJSDate(date).setZone(timezone);
    const monthDt = DateTime.fromJSDate(month).setZone(timezone);
    return dateDt.year === monthDt.year && dateDt.month === monthDt.month;
}

function dateToFirstDayOfMonth(date: Date, timezone: string): Date {
    const dt = DateTime.fromJSDate(date).setZone(timezone);
    return DateTime.fromObject(
        { year: dt.year, month: dt.month, day: 1 },
        { zone: timezone }
    ).toJSDate();
}

function addMonths(date: Date, months: number, timezone: string): Date {
    return DateTime.fromJSDate(date)
        .setZone(timezone)
        .plus({ months })
        .toJSDate();
}

function doesDateEqualOneOfDateRange(
    dateRange: [Date | null, Date | null],
    date: Date
): boolean {
    const [startDate, endDate] = dateRange;
    if (startDate && startDate.getTime() === date.getTime()) {
        return true;
    }
    if (endDate && endDate.getTime() === date.getTime()) {
        return true;
    }
    return false;
}

function doesDateRangeInterset(
    dateRange: [Date | null, Date | null],
    date: Date
): boolean {
    const [_startDate, _endDate] = dateRange;
    if (!_startDate || !_endDate) {
        return false;
    }

    // Order so that startDate is always before endDate
    const [startDate, endDate] = ensureDateRangeOrder([_startDate, _endDate]);

    if (
        date.getTime() >= startDate.getTime() &&
        date.getTime() <= endDate.getTime()
    ) {
        return true;
    }

    return false;
}

function ensureDateRangeOrder(range: [Date, Date]): [Date, Date] {
    const [start, end] = range;
    if (start.getTime() > end.getTime()) {
        return [end, start];
    }
    return range;
}

function isFirstDayOfMonth(date: Date, timezone: string): boolean {
    return DateTime.fromJSDate(date).setZone(timezone).day === 1;
}

function isLastDayOfMonth(date: Date, timezone: string): boolean {
    const dt = DateTime.fromJSDate(date).setZone(timezone);
    const nextDay = dt.plus({ days: 1 });
    return nextDay.day === 1;
}

function isDateInALaterMonth(a: Date, b: Date, timezone: string): boolean {
    const aDt = DateTime.fromJSDate(a).setZone(timezone);
    const bDt = DateTime.fromJSDate(b).setZone(timezone);
    return (
        aDt.year > bDt.year || (aDt.year === bDt.year && aDt.month > bDt.month)
    );
}
